import React, { useMemo } from 'react';
import { PhotoBand, PhotoInset } from './WrapUpPhotos';
import { Reveal, WrapUpClosing, WrapUpFigures, WrapUpNextSection, WrapUpStatement, WrapUpVoices } from './WrapUp';
import { atSeam, placePhotos, type PhotoSeam } from '../../utils/wrapUpPhotos';
import type { WrapUp } from '../../utils/wrapUp';

/**
 * THE WRAP-UP REPORT — block 2 of a finished project's page.
 *
 * **THIS DISPLAYS A DOCUMENT IT DOES NOT OWN.** Tom: *"The PDF wrap report
 * is ALL important. Stop writing it in your own words."* Every word is
 * Shilpa's, verbatim. The layout bends to the copy, never the reverse.
 *
 * THE REPORT IS DEALT, NOT DUMPED. Ten figures in one block is a wall, so
 * the bands are split around the studio's own closing paragraph:
 *
 *     statement · four figures · three figures · the closing
 *     · the rest of the figures · what they said · where this could go
 *
 * Nothing is cut to achieve that rhythm — the words are the studio's, the
 * arrangement is ours, and so are the photographs, which are dealt into
 * the seams between the bands by `placePhotos`.
 *
 * THE INK BLOCK IS WHAT HOLDS THE TWO LATER BANDS APART. With no closing
 * paragraph to sit between them they collide into one ragged grid, so the
 * split is conditional on there being one.
 *
 * There are no film tiles on a finished page. Everything is downloadable
 * by then, so the work is the "Take it all" list the page ends with rather
 * than a grid of posters to choose between.
 */
export function ReportBody({
  wrap,
  saidMark,
  nextMark
}: {
  wrap: WrapUp;
  saidMark: string;
  nextMark: string;
}) {
  const figures = wrap.numbers;
  const closingProse = wrap.closing;
  const voices = wrap.quotes;
  const forward = wrap.next;
  const openingFigures = figures.slice(0, 4);
  const afterOpening = figures.slice(4);
  const middleFigures = closingProse ? afterOpening.slice(0, 3) : afterOpening;
  const closingFigures = closingProse ? afterOpening.slice(3) : [];

  /* Where the photographs land. Pure, and worked out from the sections this
     particular report actually has. */
  const placements = useMemo(() => placePhotos(wrap.photos ?? [], {
    closing: Boolean(closingProse),
    voices: voices.length > 0,
    next: Boolean(forward)
  }), [wrap.photos, closingProse, voices.length, forward]);

  /** A band, with its own spacing, or nothing at all — never an empty gap. */
  function band(seam: PhotoSeam, className: string) {
    const placement = atSeam(placements, seam);
    if (!placement || placement.shape === 'inset') return null;
    return <div className={className} data-id="element-175" data-mp="1tbpt3var76og">
        <PhotoBand placement={placement} data-id="element-176" data-mp="1tcr6i6f8s59c" />
      </div>;
  }
  const insetPhoto = atSeam(placements, 'closing')?.photos[0];
  return <div data-id="element-177" data-mp="10uvnmkoclm7i">
      <div className="mt-12" data-id="element-178" data-mp="10ulvaqftz2yn">
        <Reveal data-id="element-179" data-mp="10tua89jv0nmm">
          <WrapUpStatement statement={wrap.statement} data-id="element-180" data-mp="10tatux9uy7ap" />
        </Reveal>
        {band('opening', 'mt-16')}
        {openingFigures.length > 0 && <div className="mt-16" data-id="element-181" data-mp="37iw0uawx48qt">
            <WrapUpFigures figures={openingFigures} data-id="element-182" data-mp="37imaunbvi1l4" />
          </div>}
      </div>

      {band('afterFilms', 'mt-20')}

      {middleFigures.length > 0 && <div className="mt-20" data-id="element-183" data-mp="2yj96ua6iq57s">
          <WrapUpFigures figures={middleFigures} data-id="element-184" data-mp="2ykal0nitz7tq" />
        </div>}

      {closingProse && <div className="mt-20" data-id="element-185" data-mp="32kuhdovky7et">
          <WrapUpClosing text={closingProse} aside={insetPhoto ? <PhotoInset photo={insetPhoto} data-id="element-187" data-mp="3qaorvzsffjvw" /> : null} data-id="element-186" data-mp="32hyhhv0698nq" />
        </div>}

      {closingFigures.length > 0 && <div className="mt-20" data-id="element-188" data-mp="3v6m722ryek73">
          <WrapUpFigures figures={closingFigures} data-id="element-189" data-mp="3ha0en7auxnk2" />
        </div>}

      <WrapUpVoices quotes={voices} n={saidMark} before={band('voices', '')} data-id="element-190" data-mp="3m8a91forck4m" />
      <WrapUpNextSection next={forward} n={nextMark} before={band('next', '')} data-id="element-191" data-mp="3maen2qgdezim" />
    </div>;
}